import { humanizeEnum } from '@/lib/format'
import type { AdminRating, RatingTag } from '@/types/api'

/**
 * What each tag code means to a person. The codes are the server's; the words
 * are ours. A code this build does not know still renders, humanised, rather
 * than vanishing from a rating that carries it.
 */
const TAG_LABELS: Record<RatingTag, string> = {
  SAFE_DRIVING: 'Safe driving',
  ON_TIME: 'On time',
  POLITE: 'Polite & friendly',
  CAREFUL_WITH_CAR: 'Careful with the car',
  GREAT_ROUTE: 'Great route',
  GOOD_COMMUNICATION: 'Good communication',
  LATE: 'Arrived late',
  DRIVING_STYLE: 'Driving style',
  ATTITUDE: 'Attitude',
  ROUTE_TAKEN: 'Route taken',
  CAR_HANDLING: 'Car handling',
  POOR_COMMUNICATION: 'Poor communication',
}

const POSITIVE_TAGS: ReadonlySet<string> = new Set<RatingTag>([
  'SAFE_DRIVING',
  'ON_TIME',
  'POLITE',
  'CAREFUL_WITH_CAR',
  'GREAT_ROUTE',
  'GOOD_COMMUNICATION',
])

export function tagLabel(tag: string): string {
  return (TAG_LABELS as Record<string, string>)[tag] ?? humanizeEnum(tag)
}

/** True for a compliment; complaints and unknown codes are false. */
export function isPositiveTag(tag: string): boolean {
  return POSITIVE_TAGS.has(tag)
}

/** Every known tag with its label, for the filter dropdown. Compliments first. */
export const TAG_OPTIONS: { value: string; label: string }[] = (Object.keys(TAG_LABELS) as RatingTag[]).map(
  (tag) => ({ value: tag, label: TAG_LABELS[tag] }),
)

/** The word under the stars in the app, so the two screens speak the same language. */
export function moodLabel(rating: number): string {
  switch (rating) {
    case 1:
      return 'Terrible'
    case 2:
      return 'Poor'
    case 3:
      return 'Okay'
    case 4:
      return 'Good'
    case 5:
      return 'Excellent'
    default:
      return '—'
  }
}

/** An average to one decimal, or a dash when there is nothing to average. */
export function formatAverage(average: number | null | undefined): string {
  if (average == null || Number.isNaN(average)) return '—'
  return average.toFixed(1)
}

/** "4.6 · 12 ratings" — the average never travels without its sample size. */
export function describeStanding(average: number | null | undefined, count: number): string {
  if (count === 0 || average == null) return 'No ratings yet'
  return `${formatAverage(average)} · ${count} ${count === 1 ? 'rating' : 'ratings'}`
}

/** "Maadi → Cairo Airport", or whichever end is known. */
export function describeJourney(rating: AdminRating): string {
  const from = rating.pickupAddress?.trim()
  const to = rating.dropoffAddress?.trim()
  if (from && to) return `${from} → ${to}`
  return from ?? to ?? '—'
}

/** The tags as a sentence for the export and for screen readers. */
export function summariseTags(tags: string[]): string {
  if (tags.length === 0) return '—'
  return tags.map(tagLabel).join(', ')
}
